// Engine voice. CarAudio talks to the engine only through EngineVoice, so the
// procedural synth below can later be replaced by a sample-based voice
// (RPM-crossfaded on/off-load loops, DESIGN.md §8) without touching the other
// layers. Shift cuts, overrun pops and levels live in CarAudio and apply to
// any voice.

import { engineCutoffHz, engineDrive, engineGain, engineHarmonics, firingHz, shaperCurve } from './mapping';
import type { NoiseBank } from './noise';

export interface EngineVoice {
  /** Connect this to the engine bus. */
  readonly output: AudioNode;
  /**
   * Called every frame.
   * @param rpm engine speed
   * @param load smoothed throttle 0..1 (on/off-load timbre)
   * @param t context time to schedule at (ctx.currentTime)
   * @param glide pitch smoothing time constant, s (short during shifts)
   */
  set(rpm: number, load: number, t: number, glide: number): void;
  /** Optional tone brightness multiplier (dev knob, 1 = default). */
  brightness?: number;
  dispose(): void;
}

export type EngineVoiceFactory = (ctx: BaseAudioContext, noise: NoiseBank) => EngineVoice;

/** Tracks the last target sent to a param so unchanged values don't add automation events. */
export class Smooth {
  private last: number;
  constructor(
    readonly param: AudioParam,
    initial: number,
  ) {
    param.value = initial;
    this.last = initial;
  }
  set(v: number, t: number, tau: number): void {
    if (!Number.isFinite(v)) return;
    if (Math.abs(v - this.last) <= 1e-4 * Math.max(1, Math.abs(v))) return;
    this.last = v;
    this.param.setTargetAtTime(v, t, tau);
  }
}

/**
 * Procedural 1.6 L V6 turbo.
 *
 * - A periodic wave at half the firing frequency (crank order 1.5) with tuned
 *   harmonics: firing orders strong, half orders weaker (V6 rasp).
 * - A detuned sawtooth at the firing frequency for brightness and beating.
 * - Firing-synchronous noise (noise ring-modulated by the sawtooth) for the
 *   combustion rasp.
 * - Slow random pitch jitter so it never sounds like a held synth note.
 * - Waveshaper grit (drive rises with throttle), then a throttle-dependent
 *   low-pass and an exhaust "formant" peak: bright on throttle, muffled off.
 */
export class SynthEngineVoice implements EngineVoice {
  readonly output: GainNode;
  /** Engine tone brightness multiplier (dev knob). */
  brightness = 1;

  private readonly oscBase: OscillatorNode;
  private readonly oscSaw: OscillatorNode;
  private readonly jitterGain: GainNode;
  private readonly sawGain: GainNode;
  private readonly rasp: GainNode;
  private readonly raspBand: BiquadFilterNode;
  private readonly drive: GainNode;
  private readonly shaper: WaveShaperNode;
  private readonly dcBlock: BiquadFilterNode;
  private readonly lowpass: BiquadFilterNode;
  private readonly formant: BiquadFilterNode;
  private readonly nodes: AudioNode[];

  private readonly fBase: Smooth;
  private readonly fSaw: Smooth;
  private readonly sDrive: Smooth;
  private readonly sCut: Smooth;
  private readonly sFormant: Smooth;
  private readonly sOut: Smooth;
  private readonly sRasp: Smooth;
  private readonly sSaw: Smooth;

  constructor(ctx: BaseAudioContext, noise: NoiseBank) {
    const amps = engineHarmonics(28);
    const imag = new Float32Array(amps.length);
    // Alternate phases so the waveform is peaky rather than a smooth saw.
    const real = new Float32Array(amps.length);
    for (let n = 1; n < amps.length; n++) {
      if (n % 3 === 0) real[n] = amps[n]!;
      else imag[n] = n % 2 ? -amps[n]! : amps[n]!;
    }
    const wave = ctx.createPeriodicWave(real, imag);

    this.oscBase = ctx.createOscillator();
    this.oscBase.setPeriodicWave(wave);
    this.oscSaw = ctx.createOscillator();
    this.oscSaw.type = 'sawtooth';
    this.oscSaw.detune.value = 9;

    this.jitterGain = ctx.createGain();
    this.jitterGain.gain.value = 14; // cents
    noise.jitter.connect(this.jitterGain);
    this.jitterGain.connect(this.oscBase.detune);
    this.jitterGain.connect(this.oscSaw.detune);

    this.sawGain = ctx.createGain();
    this.oscSaw.connect(this.sawGain);

    // Combustion rasp: band-passed noise × firing sawtooth.
    this.raspBand = ctx.createBiquadFilter();
    this.raspBand.type = 'bandpass';
    this.raspBand.frequency.value = 2400;
    this.raspBand.Q.value = 0.8;
    const raspAm = ctx.createGain();
    raspAm.gain.value = 0;
    noise.white.connect(this.raspBand);
    this.raspBand.connect(raspAm);
    this.oscSaw.connect(raspAm.gain);
    this.rasp = ctx.createGain();
    raspAm.connect(this.rasp);

    this.drive = ctx.createGain();
    this.oscBase.connect(this.drive);
    this.sawGain.connect(this.drive);
    this.rasp.connect(this.drive);

    this.shaper = ctx.createWaveShaper();
    this.shaper.curve = shaperCurve(2048, 0.35);
    this.shaper.oversample = '2x';
    this.drive.connect(this.shaper);

    this.dcBlock = ctx.createBiquadFilter();
    this.dcBlock.type = 'highpass';
    this.dcBlock.frequency.value = 70;
    this.dcBlock.Q.value = 0.6;
    this.shaper.connect(this.dcBlock);

    this.lowpass = ctx.createBiquadFilter();
    this.lowpass.type = 'lowpass';
    this.lowpass.Q.value = 1.1;
    this.dcBlock.connect(this.lowpass);

    this.formant = ctx.createBiquadFilter();
    this.formant.type = 'peaking';
    this.formant.frequency.value = 1500;
    this.formant.Q.value = 1.3;
    this.lowpass.connect(this.formant);

    this.output = ctx.createGain();
    this.formant.connect(this.output);
    // Cycle-to-cycle combustion variation: a little random amplitude wobble.
    const wobble = ctx.createGain();
    wobble.gain.value = 0.05;
    noise.jitter.connect(wobble);
    wobble.connect(this.output.gain);

    this.fBase = new Smooth(this.oscBase.frequency, firingHz(4000) / 2);
    this.fSaw = new Smooth(this.oscSaw.frequency, firingHz(4000));
    this.sDrive = new Smooth(this.drive.gain, 1);
    this.sCut = new Smooth(this.lowpass.frequency, 1000);
    this.sFormant = new Smooth(this.formant.gain, 0);
    this.sOut = new Smooth(this.output.gain, 0);
    this.sRasp = new Smooth(this.rasp.gain, 0);
    this.sSaw = new Smooth(this.sawGain.gain, 0.3);

    this.oscBase.start();
    this.oscSaw.start();
    this.nodes = [
      this.oscBase, this.oscSaw, this.jitterGain, this.sawGain, this.raspBand, raspAm, this.rasp,
      this.drive, this.shaper, this.dcBlock, this.lowpass, this.formant, this.output, wobble,
    ];
  }

  set(rpm: number, load: number, t: number, glide: number): void {
    const f = firingHz(rpm);
    this.fBase.set(f / 2, t, glide);
    this.fSaw.set(f, t, glide);
    const drive = engineDrive(rpm, load);
    // Keep the shaper output level roughly constant as drive rises.
    this.sDrive.set(drive * 0.32, t, 0.03);
    this.sCut.set(Math.min(16000, engineCutoffHz(rpm, load) * this.brightness), t, 0.03);
    this.sFormant.set(-2 + 8 * load, t, 0.04);
    this.sOut.set(engineGain(rpm, load), t, 0.03);
    this.sRasp.set(0.35 + 0.9 * load, t, 0.04);
    this.sSaw.set(0.22 + 0.25 * load, t, 0.04);
  }

  dispose(): void {
    for (const o of [this.oscBase, this.oscSaw]) {
      try {
        o.stop();
      } catch {
        // Already stopped.
      }
    }
    for (const n of this.nodes) n.disconnect();
  }
}

export const synthEngineVoice: EngineVoiceFactory = (ctx, noise) => new SynthEngineVoice(ctx, noise);
